import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';

import {
  CloneGachaCampaignMutation,
  CloneGachaCampaignMutationVariables,
  GachaCampaignStatus,
  GachaNamespace,
  GachaTemplateKind,
  GachaShopConfigDataFragment,
  GetGachaCampaignQuery,
  GetGachaCampaignQueryVariables,
  UpdateGachaCampaignMutation,
  UpdateGachaCampaignMutationVariables,
} from '../../../../../../__gqltypes__';
import { CLONE_GACHA_CAMPAIGN, GET_GACHA_CAMPAIGN, UPDATE_GACHA_CAMPAIGN } from '../graphql';
import {
  describeGachaRunningEditChanges,
  describeGachaMatcherFilters,
  formatGachaLocalTime,
  formatGachaRelative,
  formatUtc,
  GACHA_NAMESPACE_LABELS,
  GACHA_STATUS_VARIANT,
  GachaBoxDraft,
  GachaCampaignData,
  GachaCampaignDraft,
  gachaCampaignStatusAt,
  gachaDefaultStopDate,
  gachaStartPushSaveNotice,
  hasGachaCampaignStarted,
  isGachaTestBackend,
  newGachaBoxDraft,
  parseGachaAdminError,
  toGachaCampaignDraft,
  toUpdateGachaCampaignInput,
} from '../utils';
import {
  GACHA_MAX_NAME_LENGTH,
  gachaDistinctRewardCount,
  gachaMaxRolledCount,
  validateGachaCampaignDraft,
} from '../validation';
import { GachaBoxEditor } from './GachaBoxEditor';
import { GachaBoxList } from './GachaBoxList';
import { GachaMatcherFields, GachaMatcherSection } from './GachaMatcherFields';
import { GachaMysterySection } from './GachaMysterySection';
import { GachaRunningEditModal } from './GachaRunningEditModal';
import { GachaTemplateControls } from './GachaTemplateControls';
import { GachaUtcDateTime } from './GachaUtcDateTime';
import { WeightedRewardTable } from './WeightedRewardTable';

type Props = {
  campaign: GachaCampaignData;
  // The saved (or reloaded) campaign, as the server returned it.
  onCampaignUpdated: (campaign: GachaCampaignData) => void;
  // Every campaign (where the global pin is) and the shop config (the pin itself).
  allCampaigns: GachaCampaignData[];
  shopConfig: GachaShopConfigDataFragment | null;
  onShopConfigChange: (config: GachaShopConfigDataFragment) => void;
  onCloned: (clone: GachaCampaignData) => void;
  // Created in this session: the matcher sections start expanded (collapsed for a saved campaign being opened).
  isNew?: boolean;
};

// With duplicates off, the largest rolled count cannot exceed the number of distinct rewards (server rule): reward
// identities, not rows, so two Coins rows count once.
function countVsPoolErrors(box: GachaBoxDraft): string[] {
  const maxCount = gachaMaxRolledCount(box.countTable);
  const distinct = gachaDistinctRewardCount(box.rewards);
  if (box.allowDuplicates || maxCount <= distinct) {
    return [];
  }
  return [
    `Without duplicates the box cannot give ${maxCount} rewards from ${distinct} distinct rewards: ` +
      'add rewards, lower the count or allow duplicates.',
  ];
}

// Read-only, under a window field: the same instant in the browser's timezone, then the countdown. Follows the edited
// value as it is typed; the stored value stays UTC.
function WindowHint({ epochMs, nowMs, edge }: { epochMs: number; nowMs: number; edge: 'start' | 'end' }) {
  const local = formatGachaLocalTime(epochMs);
  if (!local) {
    return null;
  }
  return (
    <Form.Text className="text-muted">
      {local} · {formatGachaRelative(epochMs, nowMs, edge)}
    </Form.Text>
  );
}

type SaveFeedback =
  | { kind: 'saved' }
  | { kind: 'stale' }
  | { kind: 'invalid'; details: string[] }
  | { kind: 'error'; message: string };

/**
 * One campaign: its window, namespace, matcher and notification toggle. Saving is a full replace of the
 * campaign (boxes included) guarded by configVersion. The parent keys this component by id + configVersion, so
 * a save or a newer server version restarts the draft from the server state.
 */
export function GachaCampaignEditor({
  campaign,
  onCampaignUpdated,
  allCampaigns,
  shopConfig,
  onShopConfigChange,
  onCloned,
  isNew = false,
}: Props): JSX.Element {
  const client = useApolloClient();
  const [draft, setDraft] = useState<GachaCampaignDraft>(() => toGachaCampaignDraft(campaign));
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(() => draft.boxes[0]?._clientId ?? null);
  const [feedback, setFeedback] = useState<SaveFeedback | null>(null);
  const [reloading, setReloading] = useState(false);
  const [showClone, setShowClone] = useState(false);
  // null = closed; startedMeanwhile = the server refused an unconfirmed save because the campaign started.
  // 'running': the strict typed-name confirmation; 'notStarted': the plain save confirmation carrying the
  // start-push line (no typed name for a campaign that has not started).
  const [runningConfirm, setRunningConfirm] = useState<{
    mode: 'running' | 'notStarted';
    startedMeanwhile: boolean;
  } | null>(null);

  const [updateCampaign, { loading: saving }] = useMutation<
    UpdateGachaCampaignMutation,
    UpdateGachaCampaignMutationVariables
  >(UPDATE_GACHA_CAMPAIGN);

  // One clock for everything time-dependent in the editor: the window hints, the status badge, the RUNNING state and
  // the start-push lock all move on the same tick, so they never disagree with each other.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const savedDraft = useMemo(() => toGachaCampaignDraft(campaign), [campaign]);
  const savedInput = useMemo(() => JSON.stringify(toUpdateGachaCampaignInput(savedDraft)), [savedDraft]);
  // The saved campaign's status (the server's rule), recomputed on the tick rather than frozen at the last read.
  const status = gachaCampaignStatusAt(campaign, now);
  const isRunning = status === GachaCampaignStatus.RUNNING;
  const testBackend = isGachaTestBackend();
  const startPushLocked = hasGachaCampaignStarted(savedDraft.startDate, draft.startDate, now);
  const isDirty = JSON.stringify(toUpdateGachaCampaignInput(draft)) !== savedInput;
  const errors = useMemo(() => validateGachaCampaignDraft(draft), [draft]);

  const update = (patch: Partial<GachaCampaignDraft>) => {
    setFeedback(null);
    setDraft((previous) => ({ ...previous, ...patch }));
  };

  const [cloneCampaign, { loading: cloning }] = useMutation<
    CloneGachaCampaignMutation,
    CloneGachaCampaignMutationVariables
  >(CLONE_GACHA_CAMPAIGN);

  const clone = () => {
    setFeedback(null);
    cloneCampaign({ variables: { id: campaign.id } })
      .then(({ data }) => {
        if (data?.cloneGachaCampaign) {
          onCloned(data.cloneGachaCampaign);
        }
      })
      .catch((error) => setFeedback({ kind: 'error', message: error.message }))
      .finally(() => setShowClone(false));
  };

  const selectedBoxIndex = draft.boxes.findIndex((box) => box._clientId === selectedBoxId);
  const selectedBox = selectedBoxIndex >= 0 ? draft.boxes[selectedBoxIndex] : null;

  const setBoxes = (boxes: GachaBoxDraft[]) => update({ boxes });
  const updateBox = (updated: GachaBoxDraft) =>
    setBoxes(draft.boxes.map((box) => (box._clientId === updated._clientId ? updated : box)));
  const addBox = () => {
    const box = newGachaBoxDraft();
    setBoxes([...draft.boxes, box]);
    setSelectedBoxId(box._clientId);
  };
  const removeBox = (clientId: string) => {
    setBoxes(draft.boxes.filter((box) => box._clientId !== clientId));
    setSelectedBoxId(null);
  };

  const reload = () => {
    setReloading(true);
    client
      .query<GetGachaCampaignQuery, GetGachaCampaignQueryVariables>({
        query: GET_GACHA_CAMPAIGN,
        variables: { id: campaign.id },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        if (data?.liveOps.gachaCampaign) {
          setFeedback(null);
          // Same version (nothing saved elsewhere): the remount does not happen, drop the edits here.
          const reloaded = toGachaCampaignDraft(data.liveOps.gachaCampaign);
          setDraft(reloaded);
          setSelectedBoxId(reloaded.boxes[0]?._clientId ?? null);
          onCampaignUpdated(data.liveOps.gachaCampaign);
        }
      })
      .catch((error) => setFeedback({ kind: 'error', message: error.message }))
      .finally(() => setReloading(false));
  };

  const save = (confirmRunningEdit: boolean) => {
    setFeedback(null);
    updateCampaign({
      variables: {
        input: toUpdateGachaCampaignInput(draft),
        expectedConfigVersion: campaign.configVersion,
        confirmRunningEdit,
      },
    })
      .then(({ data }) => {
        if (data?.updateGachaCampaign) {
          onCampaignUpdated(data.updateGachaCampaign);
          setFeedback({ kind: 'saved' });
        }
      })
      .catch((error) => {
        const { code, details } = parseGachaAdminError(error.message);
        if (code === 'GACHA_ADMIN_STALE_CONFIG') {
          setFeedback({ kind: 'stale' });
        } else if (code === 'GACHA_ADMIN_RUNNING_EDIT_NOT_CONFIRMED') {
          setRunningConfirm({ mode: 'running', startedMeanwhile: true });
        } else if (code === 'GACHA_ADMIN_START_NOTIFICATION_LOCKED') {
          setFeedback({
            kind: 'error',
            message:
              'The campaign has started, so its start notification can no longer be changed. Reload to get the saved value.',
          });
        } else if (code === 'GACHA_ADMIN_INVALID_CONFIG') {
          setFeedback({ kind: 'invalid', details });
        } else {
          setFeedback({ kind: 'error', message: error.message });
        }
      });
  };

  // A RUNNING campaign (as loaded) always goes through the typed confirmation, disabling it included. A campaign
  // that has not started yet gets the plain confirmation stating whether it will send its start notification.
  const handleSave = () => {
    if (isRunning) {
      setRunningConfirm({ mode: 'running', startedMeanwhile: false });
    } else if (!startPushLocked) {
      setRunningConfirm({ mode: 'notStarted', startedMeanwhile: false });
    } else {
      save(false);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{campaign.name}</strong> <Badge variant={GACHA_STATUS_VARIANT[status]}>{status}</Badge>{' '}
          {campaign.permanent && <Badge variant="light">Permanent</Badge>}
          {isDirty && (
            <Badge variant="warning" className="ml-1">
              Unsaved changes
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center">
          <div className="mr-3">
            <Form.Check
              type="switch"
              id="gacha-campaign-enabled"
              label="Enabled (a disabled campaign is invisible whatever its dates)"
              checked={draft.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
            />
            {isRunning && !draft.enabled && (
              <Form.Text className="text-danger">
                Saving disables a running campaign: it disappears for every player at once.
              </Form.Text>
            )}
          </div>
          <Button
            variant="outline-info"
            className="mr-2"
            onClick={() => setShowClone(true)}
            disabled={cloning}
            title="Clone this campaign (a disabled copy)"
          >
            <i className="fas fa-clone mr-1" />
            Clone
          </Button>
          <Button
            variant="outline-secondary"
            className="mr-2"
            onClick={reload}
            disabled={reloading || saving}
            title="Reload the saved version (drops unsaved changes)"
          >
            {reloading ? <Spinner animation="border" size="sm" /> : <i className="fas fa-sync" />}
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving || errors.length > 0 || !isDirty}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {isRunning && (
          <Alert variant="danger">
            <i className="fas fa-exclamation-triangle mr-2" />
            <strong>This campaign is RUNNING.</strong> Players are mid-way through it (claims, refresh timers, mystery
            pity, offered mystery rewards). Every save applies to them immediately and needs a typed confirmation.
          </Alert>
        )}
        {feedback?.kind === 'saved' && (
          <Alert variant="success" dismissible onClose={() => setFeedback(null)}>
            Saved.
          </Alert>
        )}
        {feedback?.kind === 'stale' && (
          <Alert variant="danger">
            <strong>Not saved: someone else saved this campaign</strong> (another admin, or this tab is out of date).
            Reload to get the latest version, then redo your changes. Reloading drops your unsaved changes.
            <div className="mt-2">
              <Button variant="danger" size="sm" onClick={reload} disabled={reloading}>
                Reload
              </Button>
            </div>
          </Alert>
        )}
        {feedback?.kind === 'invalid' && (
          <Alert variant="danger">
            The server refused the configuration:
            <ul className="mb-0">
              {feedback.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          </Alert>
        )}
        {feedback?.kind === 'error' && <Alert variant="danger">{feedback.message}</Alert>}

        <h6 className="text-muted text-uppercase small">Campaign</h6>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor="gacha-campaign-name">
            Name
          </Form.Label>
          <Col md={6}>
            <Form.Control
              id="gacha-campaign-name"
              value={draft.name}
              maxLength={GACHA_MAX_NAME_LENGTH}
              onChange={(e) => update({ name: e.target.value })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor="gacha-campaign-namespace">
            Namespace
          </Form.Label>
          <Col md={4}>
            <Form.Control
              as="select"
              id="gacha-campaign-namespace"
              value={draft.namespace}
              onChange={(e) => update({ namespace: e.target.value as GachaNamespace })}
            >
              {Object.values(GachaNamespace).map((namespace) => (
                <option key={namespace} value={namespace}>
                  {GACHA_NAMESPACE_LABELS[namespace]}
                </option>
              ))}
            </Form.Control>
            {testBackend && (
              <Form.Text className="text-muted">On TEST, every campaign is visible regardless of namespace.</Form.Text>
            )}
          </Col>
        </Form.Group>

        <h6 className="text-muted text-uppercase small mt-3">Window (UTC+0)</h6>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor="gacha-campaign-start">
            Start (UTC)
          </Form.Label>
          <Col md={4}>
            <GachaUtcDateTime
              inputId="gacha-campaign-start"
              value={draft.startDate}
              onChange={(startDate) => update({ startDate })}
            />
            <WindowHint epochMs={draft.startDate} nowMs={now} edge="start" />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-2">
          <Col md={{ span: 9, offset: 3 }}>
            <Form.Check
              type="checkbox"
              id="gacha-campaign-permanent"
              label="Permanent (no end date)"
              checked={draft.stopDate == null}
              onChange={(e) => {
                const start = Number.isFinite(draft.startDate) ? draft.startDate : Date.now();
                update({ stopDate: e.target.checked ? null : gachaDefaultStopDate(start, testBackend) });
              }}
            />
          </Col>
        </Form.Group>
        {draft.stopDate != null && (
          <Form.Group as={Row} className="mb-2">
            <Form.Label column md={3} htmlFor="gacha-campaign-stop">
              End (UTC)
            </Form.Label>
            <Col md={4}>
              <GachaUtcDateTime
                inputId="gacha-campaign-stop"
                value={draft.stopDate}
                onChange={(stopDate) => update({ stopDate })}
              />
              <WindowHint epochMs={draft.stopDate} nowMs={now} edge="end" />
            </Col>
          </Form.Group>
        )}
        <Form.Group as={Row} className="mb-2">
          <Col md={{ span: 9, offset: 3 }}>
            <Form.Check
              type="switch"
              id="gacha-campaign-countdown"
              label="Show the campaign countdown on the boxes in the shop (default off)"
              checked={draft.showCountdown}
              onChange={(e) => update({ showCountdown: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Only the in-shop box countdown. The Shop/Home banner timer of a paid campaign does not follow it, and a
              permanent campaign never shows one.
            </Form.Text>
          </Col>
        </Form.Group>

        <h6 className="text-muted text-uppercase small mt-3">Notifications</h6>
        <Form.Group as={Row} className="mb-2">
          <Col md={{ span: 9, offset: 3 }}>
            <Form.Check
              type="switch"
              id="gacha-campaign-start-push"
              label="Send campaign-start push"
              checked={draft.startNotificationEnabled}
              disabled={startPushLocked}
              onChange={(e) => update({ startNotificationEnabled: e.target.checked })}
            />
            {startPushLocked && (
              <Form.Text className="text-info">
                <i className="fas fa-lock mr-1" />
                Locked: the campaign has started, so its start notification can no longer be turned on or off.
              </Form.Text>
            )}
            <Form.Text className="text-muted">
              Sent once when the campaign starts, to the players its matcher targets who keep &quot;New Surprise Box
              campaigns&quot; on. A BETA-only campaign never pushes (PROD and BOTH only).
              {campaign.startNotificationSentAt && ` Already sent on ${formatUtc(campaign.startNotificationSentAt)}.`}
            </Form.Text>
            {draft.namespace === GachaNamespace.BETA && draft.startNotificationEnabled && (
              <Form.Text className="text-warning">This campaign is BETA-only: no start push will be sent.</Form.Text>
            )}
          </Col>
        </Form.Group>

        <div className="mt-3">
          <GachaMatcherSection
            title="Matcher (who sees the campaign's boxes)"
            filters={describeGachaMatcherFilters(draft.matcher)}
            defaultExpanded={isNew}
          >
            <GachaMatcherFields
              idPrefix="gacha-campaign-matcher"
              matcher={draft.matcher}
              onChange={(matcher) => update({ matcher })}
            />
          </GachaMatcherSection>
        </div>

        <h6 className="text-muted text-uppercase small mt-4">Boxes (carousel order inside this campaign)</h6>
        <GachaBoxList
          campaignId={campaign.id}
          boxes={draft.boxes}
          selectedClientId={selectedBoxId}
          onSelect={setSelectedBoxId}
          onBoxesChange={setBoxes}
          onAddBox={addBox}
          allCampaigns={allCampaigns}
          shopConfig={shopConfig}
          onShopConfigChange={onShopConfigChange}
        />
        {selectedBox && (
          <div className="mt-3">
            <GachaBoxEditor
              key={selectedBox._clientId}
              box={selectedBox}
              index={selectedBoxIndex}
              campaignStartDate={draft.startDate}
              campaignStopDate={draft.stopDate}
              onChange={updateBox}
              onRemove={() => removeBox(selectedBox._clientId)}
            >
              <h6 className="text-muted text-uppercase small mt-3">Rewards</h6>
              <WeightedRewardTable
                variant="count"
                tableId={`gacha-count-${selectedBox._clientId}`}
                title="Rewards per opening"
                hint="How many rewards one opening gives, rolled first. Each row's chance is its weight / total."
                rows={selectedBox.countTable}
                onChange={(countTable) => updateBox({ ...selectedBox, countTable })}
                extraErrors={countVsPoolErrors(selectedBox)}
                headerExtra={
                  <GachaTemplateControls
                    kind={GachaTemplateKind.COUNT}
                    rows={selectedBox.countTable}
                    onLoad={(countTable) => updateBox({ ...selectedBox, countTable })}
                  />
                }
              />
              <WeightedRewardTable
                variant="rewards"
                tableId={`gacha-rewards-${selectedBox._clientId}`}
                title="Box rewards"
                hint="Each reward is then rolled from this table. Slots 1-4 are shown on the box card, then +X more."
                rows={selectedBox.rewards}
                onChange={(rewards) => updateBox({ ...selectedBox, rewards })}
                headerExtra={
                  <GachaTemplateControls
                    kind={GachaTemplateKind.REWARDS}
                    rows={selectedBox.rewards}
                    onLoad={(rewards) => updateBox({ ...selectedBox, rewards })}
                  />
                }
              />
              <GachaMysterySection
                boxClientId={selectedBox._clientId}
                mystery={selectedBox.mystery}
                campaignMatcher={draft.matcher}
                isNewCampaign={isNew}
                onChange={(mystery) => updateBox({ ...selectedBox, mystery })}
                poolHeaderExtra={
                  selectedBox.mystery && (
                    <GachaTemplateControls
                      kind={GachaTemplateKind.MYSTERY}
                      rows={selectedBox.mystery.pool}
                      onLoad={(pool) =>
                        selectedBox.mystery && updateBox({ ...selectedBox, mystery: { ...selectedBox.mystery, pool } })
                      }
                    />
                  )
                }
              />
            </GachaBoxEditor>
          </div>
        )}

        {errors.length > 0 && (
          <Alert variant="warning" className="mt-3">
            Fix these before saving:
            <ul className="mb-0">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <div className="text-muted small mt-3">
          Id {campaign.id} · version {campaign.configVersion} · created {formatUtc(campaign.created)} · updated{' '}
          {formatUtc(campaign.updatedAt)}
          {campaign.updatedBy ? ` by ${campaign.updatedBy}` : ''}
        </div>
      </Card.Body>

      <GachaRunningEditModal
        show={runningConfirm !== null}
        campaignName={campaign.name}
        changes={runningConfirm ? describeGachaRunningEditChanges(savedDraft, draft) : []}
        disabling={campaign.enabled && !draft.enabled}
        startedMeanwhile={runningConfirm?.startedMeanwhile ?? false}
        mode={runningConfirm?.mode ?? 'running'}
        startPushNotice={gachaStartPushSaveNotice(draft.startNotificationEnabled)}
        saving={saving}
        onCancel={() => setRunningConfirm(null)}
        onConfirm={() => {
          setRunningConfirm(null);
          save(true);
        }}
      />

      <Modal show={showClone} onHide={() => setShowClone(false)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Clone this campaign?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Creates <strong>{campaign.name} (copy)</strong>: disabled, with new campaign and box ids, the same window,
            matcher and boxes, and its start push not sent. Players start from zero in it.
          </p>
          {isDirty && (
            <Alert variant="warning" className="mb-0">
              The clone copies the <strong>saved</strong> version. Your unsaved changes here are not in it.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClone(false)}>
            Cancel
          </Button>
          <Button variant="info" onClick={clone} disabled={cloning}>
            {cloning ? <Spinner animation="border" size="sm" /> : 'Clone'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
